import React, {
  useEffect,
  useState
} from "react";

import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import { db } from "../../firebase";

import {
  getInventoryLedger
} from "../../services/inventoryLedgerService";

import {
  buildStockRows,
  isLowStock,
  stockTotals
} from "../../utils/stockDocs";

import {
  ledgerEffectText,
  withStoreBalance
} from "../../utils/itemLedgerBalance";

export default function InventoryDashboard() {

  // LOW-23: App.js passes no prop, so the page threw before any query.
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [
    summary,
    setSummary
  ] = useState([]);

  const [
    ledger,
    setLedger
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    stockInfo,
    setStockInfo
  ] = useState({
    attention: [],
    junkDocs: 0,
  });

  const [
    stats,
    setStats
  ] = useState({

    totalItems: 0,

    lowStock: 0,

    defectiveStock: 0,

    negativeStockItems: 0,
  });

  /* =================================================
     FETCH DASHBOARD
  ================================================= */

  const fetchDashboard =
    async () => {

      setLoading(true);

      try {

        if (!companyId) {

          throw new Error(
            "companyId required"
          );
        }

        // =========================================
        // P4-6: one row per active item (stockDocs.js)
        // =========================================

        const [
          itemsSnap,
          stockSnap
        ] = await Promise.all([

          getDocs(
            query(
              collection(db, "items"),
              where("company_id", "in", [companyId, ""])
            )
          ),

          getDocs(
            query(
              collection(db, "inventory_summary"),
              where("company_id", "==", companyId)
            )
          ),
        ]);

        const stock =
          buildStockRows({
            items: itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
            stockDocs: stockSnap.docs.map((d) => ({ id: d.id, data: d.data() })),
            companyId,
          });

        const totals =
          stockTotals(stock.rows);

        // =========================================
        // NEGATIVE STOCK AUDIT
        // =========================================

        if (
          totals.negativeStockItems > 0
        ) {

          console.error(
            "NEGATIVE STOCK DETECTED"
          );
        }

        // =========================================
        // SAVE SUMMARY
        // =========================================

        setSummary(
          stock.rows
        );

        setStockInfo(
          stock
        );

        setStats(
          totals
        );

        // =========================================
        // RECENT ACTIVITY
        // balance from the WHOLE ledger, then the
        // newest 10 rows: slicing first would give
        // wrong balances. Junk rows are dropped.
        // =========================================

        const replayLedger =
          await getInventoryLedger(
            companyId
          );

        setLedger(
          withStoreBalance(replayLedger)
            .filter(
              (entry) =>
                entry.ledger_kind !== "junk"
            )
            .slice(0, 10)
        );

      } catch (error) {

        console.error(
          "Dashboard Fetch Error:",
          error
        );

      } finally {

        setLoading(false);
      }
    };

  /* =================================================
     INITIAL LOAD
  ================================================= */

  useEffect(() => {

    fetchDashboard();

  }, []);

  /* =================================================
     LOW STOCK
  ================================================= */

  const lowStockItems =
    summary.filter(
      isLowStock
    );

  /* =================================================
     UI
  ================================================= */

  return (

    <div className="min-h-screen bg-gray-100 p-6">

      <div className="max-w-7xl mx-auto">

        {/* ===================================== */}
        {/* HEADER */}
        {/* ===================================== */}

        <h1 className="text-3xl font-bold mb-6">
          Inventory Dashboard
        </h1>

        {/* MED-35: the one-off "Run legacy item_id migration" button
            (utils/migrateLegacyItemIds.js) was removed - a migration is never
            a live button. */}

        {/* ===================================== */}
        {/* LOADING */}
        {/* ===================================== */}

        {loading ? (

          <div className="text-lg">
            Loading dashboard...
          </div>

        ) : (

          <>

            {/* ===================================== */}
            {/* STATS */}
            {/* ===================================== */}

            <div className="
              grid
              grid-cols-1
              md:grid-cols-4
              gap-6
              mb-8
            ">

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Total Items
                </h2>

                <p className="text-3xl font-bold">
                  {stats.totalItems}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Low Stock Items
                </h2>

                <p className="text-3xl font-bold text-yellow-500">
                  {stats.lowStock}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Defective Stock
                </h2>

                <p className="text-3xl font-bold text-red-500">
                  {stats.defectiveStock}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow p-6">

                <h2 className="text-gray-500 text-sm">
                  Negative Stock
                </h2>

                <p className="text-3xl font-bold text-red-600">
                  {stats.negativeStockItems}
                </p>
              </div>
            </div>

            {/* ===================================== */}
            {/* LOW STOCK ALERTS */}
            {/* ===================================== */}

            <div className="
              bg-white
              rounded-xl
              shadow
              p-6
              mb-8
            ">

              <h2 className="text-2xl font-bold mb-4">
                Low Stock Alerts
              </h2>

              <div className="overflow-x-auto">

                <table className="w-full border-collapse">

                  <thead>

                    <tr className="bg-gray-800 text-white">

                      <th className="p-3 text-left">
                        Item
                      </th>

                      <th className="p-3 text-left">
                        Code
                      </th>

                      <th className="p-3 text-left">
                        Category
                      </th>

                      <th className="p-3 text-left">
                        Qty
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {lowStockItems.length === 0 ? (

                      <tr>

                        <td
                          colSpan="4"
                          className="
                            p-4
                            text-center
                            text-gray-500
                          "
                        >
                          No low stock alerts
                        </td>
                      </tr>

                    ) : (

                      lowStockItems.map((item) => (

                        <tr
                          key={item.item_id}
                          className="border-b"
                        >

                          <td className="p-3">
                            {item.item_name || "-"}
                          </td>

                          <td className="p-3">
                            {item.item_code || "-"}
                          </td>

                          <td className="p-3">
                            {item.category || "-"}
                          </td>

                          <td className="
                            p-3
                            text-red-500
                            font-bold
                          ">
                            {item.qty || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===================================== */}
            {/* RECENT INVENTORY ACTIVITY */}
            {/* ===================================== */}

            <div className="
              bg-white
              rounded-xl
              shadow
              p-6
            ">

              <h2 className="text-2xl font-bold mb-4">
                Recent Inventory Activity
              </h2>

              <div className="overflow-x-auto">

                <table className="w-full border-collapse">

                  <thead>

                    <tr className="bg-gray-800 text-white">

                      <th className="p-3 text-left">
                        Date
                      </th>

                      <th className="p-3 text-left">
                        Item
                      </th>

                      <th className="p-3 text-left">
                        Movement
                      </th>

                      <th className="p-3 text-left">
                        Effect
                      </th>

                      <th className="p-3 text-left">
                        Balance
                      </th>
                    </tr>
                  </thead>

                  <tbody>

                    {ledger.length === 0 ? (

                      <tr>

                        <td
                          colSpan="5"
                          className="
                            p-4
                            text-center
                            text-gray-500
                          "
                        >
                          No recent inventory activity
                        </td>
                      </tr>

                    ) : (

                      ledger.map((entry) => (

                        <tr
                          key={entry.id}
                          className="border-b"
                        >

                          <td className="p-3">

                            {entry.created_at?.seconds

                              ? new Date(

                                  entry.created_at.seconds *
                                  1000

                                ).toLocaleString()

                              : "-"}
                          </td>

                          <td className="p-3">
                            {entry.item_name || "-"}
                          </td>

                          <td className="p-3">
                            {entry.movement_type || "-"}
                          </td>

                          <td className="p-3">

                            {ledgerEffectText(entry)}
                          </td>

                          <td className="p-3 font-semibold">

                            {entry.store_balance}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* P4-6: nothing is hidden without a count */}

            {stockInfo.attention.length > 0 && (

              <p className="text-sm text-amber-700 mt-4">
                {stockInfo.attention.length} items need attention and are not counted — see Stock List
              </p>
            )}

            {stockInfo.junkDocs > 0 && (

              <p className="text-sm text-gray-500 mt-4">
                {stockInfo.junkDocs} old junk records ignored (MED-22)
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}